// DOM builders of the calendar sheet: event chips, the month grid, the week columns, the phone
// agenda and the routines rail. Stateless: calendar.js passes the data and the callbacks and
// re-renders on every change. Text always goes in as text nodes; colours as CSS variables.
import { h, icon, iconButton, emptyState, badge } from '../../dom.js';
import { t, DAYS_SHORT, MONTHS_SHORT } from '../../i18n/fr.js';
import { deptColor, formatDateTime } from '../../format.js';
import { WEEK_ORDER } from '../automations/schedule.js';
import { routineNextAt } from '../automations/model.js';
import { longDayLabel, shortDayLabel } from './grid.js';
import { eventCaption, eventTone, dayOverflow } from './events.js';
import './strings.js';

const pad2 = (n) => String(n).padStart(2, '0');

function colorOf(idx, deptId) {
  return deptColor(idx.depts.get(deptId)?.color, deptId);
}

function agentLetter(name) {
  const s = String(name || '').trim();
  return s ? s[0].toUpperCase() : '';
}

/** Icon of an event by kind and tone. */
export function eventIcon(ev) {
  if (ev.kind === 'routine') return 'repeat';
  if (ev.kind === 'scheduled') return 'clock';
  if (ev.kind === 'active') return 'bolt';
  return eventTone(ev) === 'failed' || eventTone(ev) === 'blocked' ? 'alert' : eventTone(ev) === 'muted' ? 'close' : 'check';
}

/**
 * One event (routine run, scheduled / running / finished task) as a button.
 * @param {object} ev display event (events.js)
 * @param {{idx: object, onOpen: (ev: object, el: HTMLElement) => void, tabbable?: boolean}} opts
 * @returns {HTMLButtonElement}
 */
export function eventChip(ev, { idx, onOpen, tabbable = true }) {
  const agentName = idx.agents.get(ev.agentId)?.name || ev.agentName || '';
  const caption = eventCaption(ev);
  const title = ev.title || '—';
  const aria = ev.kind === 'routine' ? t('cal.ev.routineAria', { title, caption }) : t('cal.ev.aria', { title, caption });
  let el = null;
  el = h('button', {
    type: 'button',
    class: ['cal-ev', `is-${ev.kind}`, `tone-${eventTone(ev)}`, { 'is-paused': !!ev.paused, 'is-late': !!ev.late }],
    style: { '--dept': colorOf(idx, ev.deptId) },
    tabIndex: tabbable ? 0 : -1,
    title,
    'aria-label': agentName ? `${aria} — ${agentName}` : aria,
    dataset: { ev: ev.id },
    onClick: (e) => { e.stopPropagation?.(); onOpen(ev, el); },
    onKeydown: (e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation?.(); },
  },
  h('span', { class: 'cal-ev-main' }, icon(eventIcon(ev), { size: 12 }), h('span', { class: 'cal-ev-title' }, title)),
  h('span', { class: 'cal-ev-sub' },
    h('span', { class: 'cal-ev-cap' }, caption),
    agentName ? h('span', { class: 'cal-ev-agent', 'aria-hidden': 'true' }, agentLetter(agentName)) : null));
  return el;
}

function dayLabel(d, count, canSchedule) {
  const day = longDayLabel(d.ms);
  const parts = [d.today ? `${t('cal.today')}, ${day}` : day];
  if (count) parts.push(t('cal.dayEvents', { n: count }));
  return canSchedule && !d.past ? t('cal.dayAria', { day: parts.join(', ') }) : t('cal.dayAriaRO', { day: parts.join(', ') });
}

function dayNumber(d) {
  return h('div', { class: 'cal-day-num', 'aria-hidden': 'true' },
    d.first ? h('span', { class: 'cal-day-mon' }, MONTHS_SHORT[d.month]) : null,
    h('span', { class: 'cal-day-date' }, pad2(d.date)));
}

function weekdayHeader() {
  return h('div', { class: 'cal-row cal-head-row', role: 'row' },
    WEEK_ORDER.map((wd) => h('div', { class: ['cal-colhead', { 'is-weekend': wd === 0 || wd === 6 }], role: 'columnheader' },
      DAYS_SHORT[wd].replace('.', ''))));
}

/**
 * A day cell (month or week): focusable, Enter/Space or click opens the day popover.
 */
function dayCell(d, { focusKey, list, canSchedule, onDay, className, children }) {
  let cell = null;
  const open = () => onDay(d, cell);
  cell = h('div', {
    role: 'gridcell',
    class: ['cal-day', className, {
      'is-out': !d.inMonth, 'is-weekend': d.weekend, 'is-today': d.today, 'is-past': d.past, 'is-focus': d.key === focusKey,
    }],
    tabIndex: d.key === focusKey ? 0 : -1,
    dataset: { day: d.key },
    'aria-selected': String(d.key === focusKey),
    'aria-current': d.today ? 'date' : null,
    'aria-label': dayLabel(d, list.length, canSchedule),
    onClick: open,
    onKeydown: (e) => {
      if (e.target !== cell) return;
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
    },
  }, children);
  return cell;
}

/**
 * The month grid: whole weeks Monday → Sunday, routine runs (dashed), scheduled and finished tasks,
 * "N de plus" overflow, today highlighted, weekends hatched.
 * @param {{grid: object, byDay: Map<string, object[]>, focusKey: string, capacity: number, idx: object,
 *   canSchedule: boolean, onDay: Function, onEvent: Function, label: string}} m
 */
export function monthView(m) {
  const rows = m.grid.weeks.map((week) => h('div', { class: 'cal-row', role: 'row' }, week.map((d) => {
    const list = m.byDay.get(d.key) || [];
    const { visible, more } = dayOverflow(list, m.capacity);
    let cellRef = null;
    const moreBtn = more ? h('button', {
      type: 'button', class: 'cal-more', tabIndex: -1,
      'aria-label': t('cal.moreAria', { n: more, day: longDayLabel(d.ms) }),
      onClick: (e) => { e.stopPropagation?.(); m.onDay(d, cellRef); },
    }, t('cal.more', { n: more })) : null;
    cellRef = dayCell(d, {
      focusKey: m.focusKey, list, canSchedule: m.canSchedule, onDay: m.onDay,
      children: [
        h('div', { class: 'cal-day-events' }, visible.map((ev) => eventChip(ev, { idx: m.idx, onOpen: m.onEvent, tabbable: false })), moreBtn),
        dayNumber(d),
      ],
    });
    return cellRef;
  })));
  return h('div', {
    class: ['cal-grid', 'cal-month', `weeks-${m.grid.weeks.length}`, `cap-${m.capacity}`], role: 'grid', 'aria-label': m.label,
    'aria-readonly': m.canSchedule ? null : 'true',
  }, h('div', { role: 'rowgroup' }, weekdayHeader()), h('div', { class: 'cal-rows', role: 'rowgroup' }, rows));
}

/**
 * The week: seven columns listing every event of the day in time order.
 * @param {{week: object, byDay: Map<string, object[]>, focusKey: string, idx: object, canSchedule: boolean,
 *   onDay: Function, onEvent: Function, label: string}} m
 */
export function weekView(m) {
  const row = h('div', { class: 'cal-row cal-week-row', role: 'row' }, m.week.days.map((d) => {
    const list = m.byDay.get(d.key) || [];
    return dayCell(d, {
      focusKey: m.focusKey, list, canSchedule: m.canSchedule, onDay: m.onDay, className: 'cal-wday',
      children: [
        h('div', { class: 'cal-wday-head', 'aria-hidden': 'true' },
          h('span', { class: 'cal-wday-name' }, DAYS_SHORT[d.weekday].replace('.', '')),
          h('span', { class: 'cal-wday-date' }, d.first ? `${pad2(d.date)} ${MONTHS_SHORT[d.month]}` : pad2(d.date))),
        h('div', { class: 'cal-day-events' }, list.map((ev) => eventChip(ev, { idx: m.idx, onOpen: m.onEvent, tabbable: true }))),
      ],
    });
  }));
  return h('div', { class: ['cal-grid', 'cal-week'], role: 'grid', 'aria-label': m.label }, h('div', { class: 'cal-week-group', role: 'rowgroup' }, row));
}

/**
 * The phone agenda: the days of the period that hold something (and today), each with a
 * "Programmer" button when the user may schedule there.
 * @param {{days: object[], byDay: Map<string, object[]>, idx: object, canSchedule: boolean, onDay: Function,
 *   onEvent: Function}} m
 */
export function agendaView(m) {
  const sections = [];
  for (const d of m.days) {
    const list = m.byDay.get(d.key) || [];
    if (!list.length && !d.today) continue;
    let addBtn = null;
    if (m.canSchedule && !d.past) {
      addBtn = h('button', {
        type: 'button', class: 'btn btn-secondary btn-sm cal-agenda-add',
        'aria-label': t('cal.addOnDay', { day: longDayLabel(d.ms) }),
        onClick: () => m.onDay(d, addBtn),
      }, icon('plus', { size: 14 }), h('span', { class: 'btn-label' }, t('cal.mobile.add')));
    }
    sections.push(h('section', { class: ['cal-agenda-day', { 'is-today': d.today, 'is-past': d.past }], 'aria-label': longDayLabel(d.ms) },
      h('div', { class: 'cal-agenda-head' },
        h('h3', { class: 'cal-agenda-title' }, d.today ? `${t('cal.today')} · ${shortDayLabel(d.ms)}` : shortDayLabel(d.ms)),
        addBtn),
      list.length
        ? h('div', { class: 'cal-agenda-list' }, list.map((ev) => eventChip(ev, { idx: m.idx, onOpen: m.onEvent, tabbable: true })))
        : h('p', { class: 'muted cal-agenda-empty' }, t('cal.emptyDay'))));
  }
  if (!sections.length) return h('div', { class: 'cal-agenda' }, emptyState({ icon: 'calendar', title: t('cal.emptyPeriod'), text: m.canSchedule ? t('cal.agendaEmpty') : '' }));
  return h('div', { class: 'cal-agenda', role: 'list', 'aria-label': t('cal.agenda') }, sections.map((s) => h('div', { role: 'listitem' }, s)));
}

/** Is the routine waiting for the user's OK before acting (its autonomy, or its agent's)? */
export function routineNeedsOk(r, agent) {
  if (r?.autonomy === 'approve') return true;
  if (r?.autonomy === 'auto') return false;
  return agent?.autonomy !== 'auto';
}

/**
 * The routines rail: one card per routine (title, rhythm, agent, next run, paused / approval badges);
 * click = show only its runs; a settings button opens the editor for managers.
 * @param {{routines: object[], total: number, routineFilter: string|null, idx: object, now: number,
 *   status: 'loading'|'ok'|'error'|'unavailable', canManage: (deptId: string) => boolean,
 *   onFilter: (id: string) => void, onEdit: (routine: object) => void, onManage: (() => void)|null}} m
 */
export function railView(m) {
  const head = h('div', { class: 'cal-rail-head' },
    h('h2', { class: 'mono-label cal-rail-title' }, t('cal.rail.title'), ' ', h('span', { class: 'cal-rail-count' }, String(m.routines.length))),
    m.onManage ? h('button', { type: 'button', class: 'link-btn cal-rail-manage', onClick: m.onManage }, t('cal.rail.manage')) : null);
  let body;
  if (m.status === 'loading' && !m.routines.length) body = h('p', { class: 'muted cal-rail-note' }, t('cal.loading'));
  else if (m.status === 'unavailable') body = h('p', { class: 'muted cal-rail-note' }, t('cal.unavailable'));
  else if (!m.routines.length) body = h('p', { class: 'muted cal-rail-note' }, m.total ? t('cal.rail.emptyFiltered') : t('cal.rail.empty'));
  else {
    body = h('ul', { class: 'cal-rail-list' }, m.routines.map((r) => {
      const agent = m.idx.agents.get(r.agentId);
      const active = m.routineFilter === r.id;
      const next = r.paused ? null : routineNextAt(r);
      const nextText = r.paused ? t('cal.rail.paused') : next ? t('cal.rail.next', { when: formatDateTime(next, m.now) }) : t('cal.rail.noNext');
      return h('li', { class: 'cal-rail-item' },
        h('button', {
          type: 'button',
          class: ['cal-routine', { 'is-active': active, 'is-paused': !!r.paused }],
          style: { '--dept': colorOf(m.idx, r.deptId) },
          'aria-pressed': String(active),
          title: t('cal.rail.filterAria', { title: r.title || '' }),
          dataset: { fk: `routine:${r.id}` },
          onClick: () => m.onFilter(r.id),
        },
        h('span', { class: 'cal-routine-title' }, r.title || '—'),
        h('span', { class: 'cal-routine-desc' }, [r.description, agent?.name].filter(Boolean).join(' · ')),
        h('span', { class: 'cal-routine-meta' },
          h('span', { class: 'mono-label' }, nextText),
          !r.paused && routineNeedsOk(r, agent) ? badge(t('cal.rail.approve'), 'amber') : null)),
        m.canManage(r.deptId)
          ? iconButton('settings', t('cal.rail.edit', { title: r.title || '' }), { variant: 'ghost', class: 'cal-routine-edit', size: 16, onClick: () => m.onEdit(r) })
          : null);
    }));
  }
  return h('div', { class: 'cal-rail-inner' }, head, body, h('p', { class: 'cal-rail-hint' }, t('cal.rail.hint')));
}
